/**
 * TanStack Query + image-upload runtime configuration.
 *
 * Both blocks load from backend `/config` (app.yaml). Defaults below kick in
 * before config loads or when the endpoint is unreachable. Defaults must
 * stay in sync with `frontend_cache:` and `image_upload:` in app.yaml.
 */

import { useConfigStore } from '@/stores/config';
import type { AppConfigResponse, FrontendCacheConfig, LimitsConfig } from '@/api/types';

/** Default stale times (used before config loads, matches app.yaml defaults).
 *  Also used by FALLBACK_CONFIG in config.ts — import from here, not duplicate. */
export const DEFAULT_FRONTEND_CACHE: FrontendCacheConfig = {
  sessions_stale_ms: 300000,
  session_detail_stale_ms: 60000,
  users_stale_ms: 600000,
};

/** Default input limits (matches api-service-go env defaults, e.g.
 *  MAX_FEEDBACK_COMMENT_LENGTH). Shared by FALLBACK_CONFIG and the `limits`
 *  resilientBlock in schemas.ts — import from here, never duplicate. */
export const DEFAULT_LIMITS: LimitsConfig = {
  max_feedback_comment_length: 1000,
};

/** Default analytics block (telemetry only — no clinical effect). Single source
 *  of truth shared by FALLBACK_CONFIG in config.ts and the `analytics`
 *  resilientBlock in schemas.ts — import from here, never duplicate the literal. */
export const DEFAULT_ANALYTICS: AppConfigResponse['analytics'] = {
  events: [],
  export_query_log_limit: 1000,
};

/**
 * Get stale times from config store, falling back to defaults.
 * Safe to call outside React (uses getState).
 */
function getStaleConfig() {
  const config = useConfigStore.getState().config;
  return config?.frontend_cache ?? DEFAULT_FRONTEND_CACHE;
}

/**
 * Stale time values for different query types (milliseconds).
 * Reads from config on each access so changes propagate without restart.
 */
/**
 * Query options for AUTHORIZATION state — who may reach what.
 *
 * Not a tunable, which is why it is not in app.yaml's `frontend_cache:`
 * alongside the cadences below: there is no operator question "how stale may
 * an access answer be". It is a semantic, and it is declared once because it
 * has to hold at five call sites and a literal repeated five times is a drift
 * generator.
 *
 * Both halves are load-bearing together. TanStack only honours
 * `refetchOnWindowFocus` for a query it already considers STALE, so a hook that
 * sets the flag and inherits the default `sessions_stale_ms` has an inert flag
 * for five minutes — long enough to show an admin a grant list that changed
 * before they looked at it.
 */
export const AUTHORIZATION_QUERY = {
  staleTime: 0,
  refetchOnWindowFocus: true,
} as const;

export const QUERY_STALE_TIMES = {
  get sessions() { return getStaleConfig().sessions_stale_ms; },
  get sessionDetail() { return getStaleConfig().session_detail_stale_ms; },
  get users() { return getStaleConfig().users_stale_ms; },
} as const;
